import { alpha } from '@mui/material/styles';
import classNames from 'classnames';
import queryString from 'query-string';
import { Fragment, memo, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Link } from 'react-router';
import sortBy from 'sort-by';

import { CLASS_REP_GB_DISCOUNT_PERCENT } from 'my-core/class-rep-course-utils';
import { WIZE_SUBSCRIPTION_NAME } from 'my-core/constants';
import { addDate, approximateDuration, formatDate, formatDateRange, subtractDate } from 'my-core/date-utils';
import { hasContent } from 'my-core/editor-utils';
import {
  studySessionAttachmentViewed,
  // studySessionRecordingViewed,
  studySessionJoinNowClicked,
  trackCustomEvent,
} from 'my-core/gtm-events';
import { useDialogStateManager, useForceUpdate, useLoginProtected, useSearchParams } from 'my-core/hooks';
import { studentCoursePagePath } from 'my-core/routes';
import {
  ATTACHMENT_TYPES_CONFIG,
  getStudySessionDateDateStatus,
  getStudySessionDateLabel,
  getStudySessionDateStatus,
  getStudySessionEndTime,
  getStudySessionIncludedFeatures,
  getStudySessionInstructor,
  getStudySessionPricing,
  getStudySessionStartTime,
  isStudySessionListable,
  isStudySessionUserRegistered,
  STUDY_SESSION_TYPES,
  useRegisterFreeStudySession,
} from 'my-core/study-session-utils';
import {
  getIsActiveOrTrialing,
  getSubscriptionProductDiscountPercent,
  SUBSCRIPTION_PRODUCT_DISCOUNT_PERCENT,
} from 'my-core/subscription-utils';
import { clamp, formatSeekTime, numberToCurrency, pluralize } from 'my-utils';

import LoginProtected from 'my-components/LoginProtected';
import { StudySessionCheckout, StudySessionFreeConfirmationDialog } from 'my-components/StudySession';
import StudySessionCardActionButton from 'my-components/StudySession/CoursePageStudySessionCard/StudySessionCardActionButton';
import { GradeBoosterPartner } from 'my-components/StudySession/GradeBoosterPartners';
import StudySessionCoverArt from 'my-components/StudySession/StudySessionCoverArt';
import SubscriptionCheckout from 'my-components/SubscriptionCheckout';
import { AspectRatioWrapper } from 'my-components/VideoPlayer';
import Editor from 'my-editor/Editor';
import { LoadingFallback, pluginPresets } from 'my-editor/index';
import ButtonXL from 'my-elements/ButtonXL';
import DropDownButton from 'my-elements/DropDownButton';
import InstructorInfo from 'my-elements/InstructorInfo';
import Label from 'my-elements/Label';

import { getWeeklyBundleGroupings } from '../student-course-page-utils';
import { StudentCoursePageFAQsList } from '../StudentCoursePageCommon';
import StudentCoursePageStudySessionVideoPlayer from './StudentCoursePageStudySessionVideoPlayer';

import Box from '@mui/material/Box';
import Button, { buttonClasses } from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import MuiLink from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { faArrowDownToLine, faBadgeCheck, faCalendar, faCheck, faDownload } from '@fortawesome/pro-regular-svg-icons';
import { faArrowLeft, faCaretDown, faCircleInfo, faHourglassClock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function StudentCoursePageStudySession({
  course,
  courseSubscription,
  currentUser,
  studySession: studySessionProp,
  studySessions,
  subscription,
  userCourse,
}) {
  useEffect(() => {
    trackCustomEvent(
      'Grade-Booster Page Viewed',
      {
        course_code: course.code,
        course_id: course.id,
        school_name: course.school?.name,
        school_id: course.school_id,
        study_session_id: studySessionProp.id,
        study_session_type: studySessionProp.type,
      },
      { custIO: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const weeklyBundleGrouping = useMemo(
    () =>
      studySessionProp.type === 'weekly_tutorial' ?
        getWeeklyBundleGroupings(studySessions).weeklyBundleGroupings.find(g =>
          g.some(ss => ss.id === studySessionProp.id),
        )
      : undefined,
    [studySessions, studySessionProp.type, studySessionProp.id],
  );

  const cacheTimestamp = Math.floor(Date.now() / (5 * 60_000));
  const { generalAttachments, navItems, studySession } = useMemo(() => {
    const _ = cacheTimestamp;
    const primaryStudySession = weeklyBundleGrouping?.[0] || studySessionProp;
    const allSessions = weeklyBundleGrouping || [studySessionProp];

    const attsBySSDAndType = {};
    allSessions.forEach(ss => {
      [...ss.attachments].sort(sortBy('order', 'name')).forEach(att => {
        const ssdId = (ss.dates.length === 1 ? ss.dates[0].id : ss.dates.length > 1 && att.ssd_id) || null;
        (((attsBySSDAndType[ss.id] ||= {})[ssdId] ||= {})[`${att.type}s`] ||= []).push(att);
      });
    });
    const dateLabel = getStudySessionDateLabel(primaryStudySession);
    const dateStatus = getStudySessionDateStatus(primaryStudySession);
    const startTime = getStudySessionStartTime(primaryStudySession);

    const generalAttachments = primaryStudySession.dates.length > 1 &&
      !(primaryStudySession.booklet_excluded && primaryStudySession.solutions_excluded) && {
        ...attsBySSDAndType[primaryStudySession.id]?.[null],
        booklet_release_date:
          !primaryStudySession.booklet_excluded &&
          subtractDate(startTime, {
            days: primaryStudySession.disable_zoom ? 0 : 1,
          }),
        solutions_release_date:
          !primaryStudySession.solutions_excluded &&
          (primaryStudySession.disable_zoom && !primaryStudySession.location?.address ?
            new Date(startTime)
          : new Date(primaryStudySession.dates[0].end_time)),
      };

    const dates = allSessions.flatMap(ss => ss.dates.map(ssd => [ss, ssd])).sort(sortBy('1.start_time'));

    const datelessNavItems = allSessions
      .flatMap(ss => {
        return ss.recording_excluded ?
            []
          : (attsBySSDAndType[ss.id]?.[null]?.recordings || (ss.dates.length ? [] : [null])).map(att => [ss, att]);
      })
      .sort(sortBy('0.price'));

    const hasMultipleDates = dates.length > 1;
    const navItems = [
      ...datelessNavItems.map(([ss, att], idx) => ({
        label: `Part ${idx + 1}`,
        study_session: ss,
        ssd: ss.dates.length > 1 ? undefined : ss.dates[0],
        recording: att,
        booklets: (ss !== primaryStudySession || !generalAttachments) && attsBySSDAndType[ss.id]?.[null]?.booklets,
        solutions: (ss !== primaryStudySession || !generalAttachments) && attsBySSDAndType[ss.id]?.[null]?.solutions,
        date_status: dateStatus,
      })),
      ...dates.flatMap(([ss, ssd], i) => {
        const isMultiDate = ss.dates.length > 1;
        const attachments = attsBySSDAndType[ss.id]?.[ssd.id];
        const recordingExcluded = isMultiDate ? ssd.recording_excluded : ss.recording_excluded;
        const dateStatus = getStudySessionDateDateStatus(ssd, ss);
        return ((!recordingExcluded && attachments?.recordings) || [null]).map((att, j) => ({
          label: `${dateLabel} ${(hasMultipleDates ? i : j) + 1}${hasMultipleDates && attachments?.recordings?.length > 1 ? (j + 10).toString(36) : ''}`,
          study_session: ss,
          ssd,
          recording: att,
          booklets: attachments?.booklets,
          solutions: attachments?.solutions,
          date_status: dateStatus,
          recording_excluded: recordingExcluded,
        }));
      }),
    ].map(i => ({ ...i, ...getItemReleaseInfo(i) }));

    return {
      studySession: primaryStudySession,
      generalAttachments,
      navItems,
    };
  }, [cacheTimestamp, studySessionProp, weeklyBundleGrouping]);

  const [searchParams, setSearchParams] = useSearchParams();

  // FREE STUDY-SESSION REGISTRATION HANDLING
  const [registerFreeSession, studySessionFreeRegisterStatus] = useRegisterFreeStudySession(course);
  const [freeConfDialogOpen, freeConfDialogSSUserId, freeConfDialogActions] = useDialogStateManager();
  useEffect(() => {
    if (!studySessionFreeRegisterStatus?.success) return;
    if (studySession.purchase_type === 'subscription') return;
    freeConfDialogActions.open(studySessionFreeRegisterStatus.id);
  }, [studySessionFreeRegisterStatus, freeConfDialogActions, studySession.purchase_type]);

  // PAID STUDY-SESSION REGISTRATION HANDLING
  const canFreeRegister =
    (studySession.purchase_type === 'standard' && !studySession.price) ||
    (studySession.purchase_type === 'subscription' && getIsActiveOrTrialing(subscription));
  const checkoutShouldOpen = searchParams.checkout;
  const isRegistered = isStudySessionUserRegistered(studySession.current_user_study_session_user);

  const loginProtected = useLoginProtected();
  const [checkoutOpen, checkoutOpened, checkoutActions] = useDialogStateManager();
  useEffect(() => {
    if (checkoutShouldOpen && !isRegistered) {
      loginProtected(() => (canFreeRegister ? registerFreeSession(studySession.id) : checkoutActions.open(true)), {
        actionText: 'Register',
        messagingProduct: 'live-prep',
        source: 'checkout_search_param',
        onClose: () => setSearchParams({ checkout: undefined }, true),
      });
    } else {
      checkoutActions.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ignore isRegistered so doesn't close automatically upon registration
  }, [checkoutActions, studySession.id, checkoutShouldOpen, loginProtected, setSearchParams]);
  const openCheckoutForm = useCallback(() => setSearchParams({ checkout: true }, true), [setSearchParams]);

  const feedbackOpen = useMemo(() => {
    const _ = cacheTimestamp;
    if (!isRegistered) return false;
    const curDate = new Date();
    if (navItems.some(i => i.recording && i.release_date < curDate)) return true;
    if (!studySession.disable_zoom) {
      const feedbackReleaseTime = curDate - 30 * 60_000; // 30 minutes ago
      if (navItems.some(i => i.ssd && new Date(i.ssd.start_time) < feedbackReleaseTime)) return true;
    }
    return false;
  }, [isRegistered, navItems, studySession, cacheTimestamp]);

  const instructor = getStudySessionInstructor(studySession);
  const [activeItemIdx, setActiveItemIdx] = useState(() => {
    if (navItems.length <= 1 || studySession.type !== 'weekly_tutorial') return 0;
    const curDate = new Date();
    return ~(
      (isRegistered &&
        (~navItems.findIndex(i => i.is_live_now) || ~navItems.findLastIndex(i => i.release_date < curDate))) ||
      -1
    );
  });

  const activeItem = navItems[activeItemIdx];

  return (
    <>
      <ExamPrepWarningBanner studySession={studySessionProp} studySessions={studySessions} />
      <div
        css={({ breakpoints, shape: { borderRadius }, spacing }) => ({
          padding: spacing(3, 3, 5),
          flex: 1,
          overflow: 'auto',
          width: '100%',
          maxWidth: 1440,
          margin: spacing(0, 'auto'),
          display: 'flex',
          alignItems: 'flex-start',
          gap: spacing(3),

          '& > main, & > aside': {
            containerType: 'inline-size',
            display: 'flex',
            flexDirection: 'column',
            gap: spacing(3),
          },
          '& > main': {
            flex: 1,
            '& .videoWrapper': {
              borderRadius,
              overflow: 'hidden',
            },
          },
          '& > aside': { flex: '0 0 400px' },
          [breakpoints.down('md')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
            flex: '0 0 100%',
            padding: spacing(2, 2, 5),
            containerType: 'inline-size',
            '& > main, & > aside': { display: 'contents', containerType: 'unset' },
            '& .faqsSection': { order: 99 },
          },
          [breakpoints.down('sm')]: { padding: spacing(1, 1, 5) },
        })}
      >
        <main className="mainContent">
          <div>
            <Typography sx={{ color: 'text.secondary', '& svg': { mr: 1 } }} variant="overline">
              <Link style={{ color: 'inherit' }} to={studentCoursePagePath(course)}>
                <FontAwesomeIcon icon={faArrowLeft} /> All Resources for {course.code}
              </Link>
            </Typography>
            <Typography sx={{ mt: 1, mb: 2 }} variant="h4">
              {`${course.code} ${studySession.title}${navItems.length > 1 && activeItem ? `: ${activeItem.label}` : ''}`}
            </Typography>
            <VideoSection
              key={`${activeItem.label}_${activeItem.study_session.current_user_study_session_user?.id}`}
              activeItem={activeItem}
              course={course}
              currentUser={currentUser}
              instructor={instructor}
              isBundleRegistered={isRegistered}
              navItems={navItems}
              onRegister={openCheckoutForm}
              onRegisterFree={registerFreeSession}
              subscription={subscription}
            />
          </div>
          <FAQsSection className="faqsSection" studySession={studySession} />
        </main>
        <aside>
          <StudySessionInfoSection
            activeItemIdx={activeItemIdx}
            course={course}
            courseSubscription={courseSubscription}
            currentUser={currentUser}
            generalAttachments={generalAttachments}
            instructor={instructor}
            navItems={navItems}
            onRegister={openCheckoutForm}
            onRegisterFree={registerFreeSession}
            onSelectItemIdx={setActiveItemIdx}
            studySession={studySession}
            studySessionFreeRegisterStatus={studySessionFreeRegisterStatus}
            subscription={subscription}
            userCourse={userCourse}
            weeklyBundleGrouping={weeklyBundleGrouping}
          />
          {feedbackOpen && (
            <StudySessionFeedbackCard
              course={course}
              currentUser={currentUser}
              instructor={instructor}
              studySession={studySession}
            />
          )}
          <OtherStudySessions studySession={studySession} studySessions={studySessions} />
        </aside>
        {freeConfDialogSSUserId && (
          <StudySessionFreeConfirmationDialog
            onClose={freeConfDialogActions.close}
            onExited={freeConfDialogActions.clear}
            open={freeConfDialogOpen}
            studySessionUserId={freeConfDialogSSUserId}
          />
        )}
        {checkoutOpened &&
          (studySession.purchase_type === 'standard' ?
            <StudySessionCheckout
              key={studySession.id}
              courseId={course.id}
              onClose={() => setSearchParams({ checkout: undefined }, true)}
              open={checkoutOpen}
              studySessionId={studySession.id}
            />
          : studySession.purchase_type === 'subscription' && (
              <SubscriptionCheckout
                onClose={() => setSearchParams({ checkout: undefined }, true)}
                open={checkoutOpen}
              />
            ))}
      </div>
    </>
  );
}

function VideoSection({
  activeItem,
  course,
  currentUser,
  instructor,
  isBundleRegistered,
  navItems,
  onRegister,
  onRegisterFree,
  subscription,
}) {
  const [previewEnded, togglePreviewEnded] = useReducer(previewEnded => !previewEnded, false);
  const recording = activeItem.recording;
  const studySession = activeItem.study_session;
  const studySessionUser = studySession.current_user_study_session_user;
  const isRegistered = isStudySessionUserRegistered(studySessionUser);
  const isSubscriptionOffering = studySession.purchase_type === 'subscription';
  const canFreeRegister =
    !isRegistered &&
    ((studySession.purchase_type === 'standard' && !studySession.price) ||
      (isSubscriptionOffering && getIsActiveOrTrialing(subscription)));

  const purchaseCTAProps = !isRegistered && {
    onClick: canFreeRegister ? () => onRegisterFree(studySession.id) : onRegister,
    children:
      canFreeRegister ? 'Register for Free'
      : isSubscriptionOffering ? `Subscribe ${recording ? 'for Full Video' : 'to Register'}`
      : 'Purchase Now',
  };

  const loginProtectedProps = {
    actionText: 'Register',
    messagingProduct: 'live-prep',
    source: 'video_player_cta',
  };

  const coverArt = (
    <StudySessionCoverArt
      course={course}
      instructor={instructor}
      partLabel={navItems.length > 1 && activeItem.label}
      studySession={studySession}
    />
  );

  if (activeItem.is_live_now) {
    const joinLink =
      isRegistered && (studySessionUser?.zoom_info?.[activeItem.ssd.id]?.join_url || studySession.meeting_link);
    return (
      <VideoCoverArtOverlay coverArt={coverArt}>
        {isRegistered ?
          joinLink ?
            <ButtonXL
              component="a"
              href={joinLink}
              nativeButton={false}
              onClick={() => studySessionJoinNowClicked({ course, ...studySession })}
              rel="noreferrer"
              target="_blank"
              variant="contained"
            >
              Join Now
            </ButtonXL>
          : <Typography sx={{ p: 2, maxWidth: 500, typography: ['subtitle1', 'h6'] }} variant="h6">
              There was an issue generating your Zoom link. Please contact support for assistance.
            </Typography>

        : <LoginProtected {...loginProtectedProps}>
            <ButtonXL {...purchaseCTAProps} variant="contained" />
          </LoginProtected>
        }
        <div
          css={({ palette, spacing, typography }) => ({
            position: 'absolute',
            left: 0,
            width: '100%',
            bottom: 0,
            padding: spacing(1),
            backgroundColor: palette.secondary.main,
            ...typography.subtitle1,
          })}
        >
          ⏺ LIVE NOW
        </div>
      </VideoCoverArtOverlay>
    );
  }

  if (activeItem.live_start_time) {
    if (activeItem.live_start_time > new Date()) {
      return (
        <AspectRatioWrapper className="videoWrapper">
          {coverArt}
          <div
            css={({ spacing }) => ({
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              padding: spacing(3, 4),
              display: 'flex',
              gap: spacing(2),
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'rgba(0,0,0,0.6)',
              '@container (max-width: 500px)': { flexDirection: 'column', gap: 0, padding: spacing(1, 2) },
            })}
          >
            <Typography sx={{ color: 'common.white' }} variant="h6">
              Upcoming Live
            </Typography>
            <VideoCountdownTimer date={activeItem.live_start_time} />
          </div>
        </AspectRatioWrapper>
      );
    }
    return (
      <AspectRatioWrapper className="videoWrapper">
        {coverArt}
        <div
          css={({ palette, spacing }) => ({
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            backgroundColor: palette.grey[200],
            textAlign: 'center',
            padding: spacing(3),
          })}
        >
          <Typography variant="subtitle1">Live Session Ended</Typography>
          <Typography>Sorry, this session was not recorded</Typography>
        </div>
      </AspectRatioWrapper>
    );
  }
  if (activeItem.release_date) {
    if (activeItem.release_date > new Date()) {
      return (
        <AspectRatioWrapper className="videoWrapper">
          {coverArt}
          {!studySession.disable_zoom && activeItem.ssd ?
            <div
              css={({ palette, spacing }) => ({
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                backgroundColor: palette.grey[200],
                textAlign: 'center',
                padding: spacing(3),
              })}
            >
              <Typography variant="subtitle1">Live Session Ended</Typography>
              <Typography>Recording will be released within 24 hours</Typography>
            </div>
          : <div
              css={({ spacing }) => ({
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                padding: spacing(3, 4),
                display: 'flex',
                gap: spacing(2),
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'rgba(0,0,0,0.6)',
                '@container (max-width: 500px)': { flexDirection: 'column', gap: 0, padding: spacing(1, 2) },
              })}
            >
              <Typography sx={{ color: 'common.white' }} variant="h6">
                Video Releases Soon
              </Typography>
              <VideoCountdownTimer date={activeItem.release_date} />
            </div>
          }
        </AspectRatioWrapper>
      );
    }
    if (recording) {
      const continueActionText =
        canFreeRegister ? 'Register'
        : isSubscriptionOffering ? 'Subscribe'
        : 'Purchase';
      if (previewEnded) {
        return (
          <VideoCoverArtOverlay coverArt={coverArt}>
            <Typography variant="h6">Preview Ended</Typography>
            <Typography>{`${continueActionText} to continue watching`}</Typography>
            <LoginProtected {...loginProtectedProps}>
              <Button {...purchaseCTAProps} variant="contained" />
            </LoginProtected>
          </VideoCoverArtOverlay>
        );
      }
      if (!isRegistered && recording.urls?.drm_protected && !recording.urls.preview) {
        return (
          <VideoCoverArtOverlay coverArt={coverArt}>
            <Typography variant="h6">No Preview Available</Typography>
            <Typography>{`${continueActionText} to watch`}</Typography>
            <LoginProtected {...loginProtectedProps}>
              <Button {...purchaseCTAProps} variant="contained" />
            </LoginProtected>
          </VideoCoverArtOverlay>
        );
      }

      if (isRegistered && recording.urls?.drm_protected && !recording.urls.hls) {
        return (
          <VideoCoverArtOverlay coverArt={coverArt}>
            <Typography variant="h6">Recording Processing...</Typography>
            <Typography>Please refresh the page in a few minutes</Typography>
          </VideoCoverArtOverlay>
        );
      }

      return (
        <StudentCoursePageStudySessionVideoPlayer
          className="videoWrapper"
          coverArt={coverArt}
          currentUser={currentUser}
          isRegistered={isBundleRegistered || isRegistered}
          onTogglePreviewEnded={togglePreviewEnded}
          recording={recording}
          studySession={studySession}
          studySessionUser={studySessionUser}
        />
      );
    }

    return (
      <AspectRatioWrapper className="videoWrapper">
        {coverArt}
        <div
          css={({ palette, spacing }) => ({
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            backgroundColor: palette.grey[200],
            textAlign: 'center',
            padding: spacing(3),
          })}
        >
          <Typography variant="subtitle1">Sorry for the delay!</Typography>
          <Typography>This recording will be released momentarily</Typography>
        </div>
      </AspectRatioWrapper>
    );
  }

  return <AspectRatioWrapper className="videoWrapper">{coverArt}</AspectRatioWrapper>;
}

function VideoCoverArtOverlay({ children, coverArt }) {
  return (
    <AspectRatioWrapper className="videoWrapper">
      {coverArt}
      <div
        css={({ palette, spacing }) => ({
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: alpha(palette.grey[900], 0.7),
          backdropFilter: 'blur(5px)',
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(2),
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: palette.common.white,
        })}
      >
        {children}
      </div>
    </AspectRatioWrapper>
  );
}

function VideoCountdownTimer({ date }) {
  const minutesToDate = (date.valueOf() - Date.now()) / (1000 * 60);
  const days = Math.floor(minutesToDate / 60 / 24);
  const hours = Math.floor((minutesToDate / 60) % 24);
  const minutes = Math.ceil(minutesToDate % 60);
  const forceUpdate = useForceUpdate();
  useEffect(() => {
    let timeout = null;
    function refreshAndReschedule() {
      forceUpdate();
      resetTimeout();
    }
    function resetTimeout() {
      timeout = setTimeout(refreshAndReschedule, 1000 * 60 - (Date.now() % (1000 * 60)));
    }
    resetTimeout();
    return () => clearTimeout(timeout);
  }, []);
  return (
    <div
      css={({ palette, spacing, typography }) => ({
        flex: 0,
        display: 'flex',
        alignItems: 'baseline',
        gap: spacing(2),
        '& .timePart': { display: 'flex', flexDirection: 'column', alignItems: 'center' },
        '& .timePartNum': {
          ...typography.h2,
          fontSize: `min(${typography.h2.fontSize}px, 8cqw)`,
          color: palette.primary.main,
        },
        '& .timePartLabel': {
          color: palette.common.white,
          ...typography.overline,
          fontSize: `min(${typography.overline.fontSize}px, 2.5cqw)`,
        },
        '@container (max-width: 500px)': { gap: spacing(1) },
      })}
    >
      {[!!days && [days, 'days'], [hours, 'hours'], [minutes, 'minutes']].filter(Boolean).map(([num, label], i, a) => (
        <Fragment key={label}>
          <div className="timePart">
            <span className="timePartNum">{`${num}`.padStart(2, 0)}</span>
            <span className="timePartLabel">{label}</span>
          </div>
          {i < a.length - 1 && <span className="timePartNum">:</span>}
        </Fragment>
      ))}
    </div>
  );
}

function StudySessionInfoSection({
  activeItemIdx,
  course,
  courseSubscription,
  currentUser,
  generalAttachments,
  instructor,
  navItems,
  onRegister,
  onRegisterFree,
  onSelectItemIdx,
  studySession,
  studySessionFreeRegisterStatus,
  subscription,
  userCourse,
  weeklyBundleGrouping,
}) {
  const activeNavItem = navItems[activeItemIdx];
  const typeConfig = STUDY_SESSION_TYPES[studySession.type];
  const isRegistered = isStudySessionUserRegistered(studySession.current_user_study_session_user);
  const showGenAttachments = isRegistered && !!generalAttachments;
  const showItemAttachments =
    (activeNavItem.booklet_release_date || activeNavItem.solutions_release_date) &&
    (isRegistered || isStudySessionUserRegistered(activeNavItem.study_session.current_user_study_session_user));

  return (
    <Paper elevation={0} sx={{ overflow: 'hidden', flexShrink: 0 }}>
      {isRegistered && (
        <Box
          sx={{
            backgroundColor: 'background.paper2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 1,
            color: 'text.secondary',
          }}
        >
          <FontAwesomeIcon icon={faBadgeCheck} />
          <Typography variant="overline">Purchased</Typography>
        </Box>
      )}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h5">
          {course.code} {studySession.title}
        </Typography>
        {studySession.partner_program && (
          <GradeBoosterPartner customPartner={studySession.custom_partner} partnerKey={studySession.partner_program} />
        )}
        {!isRegistered && (
          <StudySessionPricingBlock
            activeNavItem={activeNavItem}
            courseSubscription={courseSubscription}
            currentUser={currentUser}
            navItems={navItems}
            onRegister={onRegister}
            onRegisterFree={onRegisterFree}
            onSelectItemIdx={onSelectItemIdx}
            studySession={studySession}
            studySessionFreeRegisterStatus={studySessionFreeRegisterStatus}
            subscription={subscription}
            userCourse={userCourse}
            weeklyBundleGrouping={weeklyBundleGrouping}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {renderSingleNavItemInfo()}
          <Typography component="div" variant="body2">
            {hasContent(studySession.description) ?
              <Editor
                fallback={<LoadingFallback />}
                plugins={pluginPresets.ALL}
                readOnly
                value={studySession.description}
              />
            : typeConfig.description}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
            {getStudySessionIncludedFeatures(studySession).map(({ key, label }) => (
              <Typography key={key} variant="body2">
                <FontAwesomeIcon icon={faCheck} />
                {` ${label}`}
              </Typography>
            ))}
          </Box>
          {instructor && <InstructorInfo instructor={instructor} />}
          {(showGenAttachments || showItemAttachments) && (
            <DropDownButton
              button={
                <Button
                  endIcon={<FontAwesomeIcon icon={faCaretDown} />}
                  fullWidth
                  startIcon={<FontAwesomeIcon icon={faDownload} />}
                  sx={{ [`& > .${buttonClasses.endIcon}`]: { ml: 'auto' } }}
                  variant="outlined"
                >
                  Download Resources
                </Button>
              }
            >
              <List
                sx={{
                  '& .attachment': {
                    gap: 2,
                    color: 'text.secondary',
                    '&.attachment_active': { color: 'primary.main' },
                  },
                }}
              >
                {showItemAttachments &&
                  renderAttachmentsGroup(
                    activeNavItem.study_session.dates.length > 1 && `${activeNavItem.label} Resources`,
                    activeNavItem,
                    activeNavItem.study_session.id,
                  )}
                {showGenAttachments && renderAttachmentsGroup('General Resources', generalAttachments, studySession.id)}
              </List>
            </DropDownButton>
          )}
        </Box>
      </Box>
      <StudySessionItemsNavigation
        activeItemIdx={activeItemIdx}
        course={course}
        instructor={instructor}
        navItems={navItems}
        onSelectItemIdx={onSelectItemIdx}
        studySession={studySession}
      />
    </Paper>
  );

  function renderAttachmentsGroup(heading, attachmentsOwner, ssId) {
    return (
      <>
        {heading && (
          <ListItem>
            <ListItemText primary={heading} slotProps={{ primary: { variant: 'subtitle1' } }} />
          </ListItem>
        )}
        {renderAttachmentListItems(attachmentsOwner.booklet_release_date, attachmentsOwner.booklets, 'booklet', ssId)}
        {renderAttachmentListItems(
          attachmentsOwner.solutions_release_date,
          attachmentsOwner.solutions,
          'solution',
          ssId,
        )}
      </>
    );
  }
  function renderAttachmentListItems(releaseDate, attachments, attType, ssId) {
    const upcomingRelease = releaseDate > new Date();
    if (!releaseDate) return;
    return upcomingRelease || !attachments ?
        <ListItem className="attachment" dense>
          <FontAwesomeIcon icon={ATTACHMENT_TYPES_CONFIG[attType].icon} />
          <ListItemText
            primary={attachments?.[0]?.name || `${attType}.pdf`}
            secondary={`Releases ${upcomingRelease ? formatDate(releaseDate, 'upcomingDateAndTime') : 'soon'}`}
          />
          <FontAwesomeIcon icon={faArrowDownToLine} />
        </ListItem>
      : attachments.map(att => (
          <ListItemButton
            key={att.id}
            className="attachment attachment_active"
            component="a"
            dense
            href={`/api/v1/study_sessions/${ssId}/${attType}s/${att.attachment_id}${attType === 'booklet' ? `/${encodeURIComponent(att.name)}` : ''}`}
            nativeButton={false}
            onClick={() => studySessionAttachmentViewed({ course, ...studySession }, att)}
            target="_blank"
          >
            <FontAwesomeIcon icon={ATTACHMENT_TYPES_CONFIG[attType].icon} />
            <ListItemText primary={att.name} />
            <FontAwesomeIcon icon={faArrowDownToLine} />
          </ListItemButton>
        ));
  }

  function renderSingleNavItemInfo() {
    const activeNavItemDate = activeNavItem?.live_start_time || activeNavItem?.release_date;
    if (!(navItems.length === 1 && activeNavItemDate)) return;
    const curDate = new Date();
    if (activeNavItemDate < curDate) return;
    const prefix = activeNavItem?.live_start_time > curDate ? 'Live' : 'Releases';

    return (
      <Box
        sx={{
          typography: 'body2',
          display: 'flex',
          alignItems: 'center',
          columnGap: 1,
          '& svg': { color: 'text.secondary' },
        }}
      >
        {activeNavItem.estimated_duration && (
          <>
            <div>
              <FontAwesomeIcon icon={faHourglassClock} />{' '}
              {pluralize(
                ...approximateDuration(activeNavItem.estimated_duration, { excludeDays: true, shortName: true }),
              )}
            </div>
            <span>|</span>
          </>
        )}
        <div>
          <FontAwesomeIcon icon={faCalendar} /> {`${prefix}: ${formatDate(activeNavItemDate, 'upcomingDateAndTime')}`}
        </div>
      </Box>
    );
  }
}

function StudySessionPricingBlock({
  activeNavItem,
  courseSubscription,
  currentUser,
  navItems,
  onRegister,
  onRegisterFree,
  onSelectItemIdx,
  studySession,
  studySessionFreeRegisterStatus,
  subscription,
  userCourse,
  weeklyBundleGrouping,
}) {
  const discountPercent =
    currentUser.has_class_rep_discount ? CLASS_REP_GB_DISCOUNT_PERCENT : (
      getSubscriptionProductDiscountPercent(subscription)
    );
  const isSubscriptionOffering = studySession.purchase_type === 'subscription';
  const [price, regularPrice] = getStudySessionPricing(studySession, discountPercent);
  const discountAmt = !!regularPrice && regularPrice - price;
  const discountStr =
    discountAmt &&
    (discountAmt > 20 ? numberToCurrency(discountAmt) : `${((discountAmt * 100) / regularPrice).toFixed()}%`);
  const [subscriptionCheckoutDialogOpen, subscriptionCheckoutDialogOpened, subscriptionCheckoutDialogActions] =
    useDialogStateManager();

  return (
    <>
      <Box
        sx={{
          borderRadius: 1,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <div>
          <Typography
            sx={{
              textAlign: 'center',
              color: regularPrice ? 'orange.main' : 'text.primary',
              '& > .reg-price': { color: 'text.secondary', textDecoration: 'line-through' },
            }}
            variant="h6"
          >
            {!!regularPrice && <span className="reg-price">{numberToCurrency(regularPrice)}</span>}
            {` ${
              price ? numberToCurrency(price)
              : isSubscriptionOffering ? `Included with ${WIZE_SUBSCRIPTION_NAME}`
              : 'Free Session'
            }`}
          </Typography>
          {!!regularPrice && !!price && (
            <Typography sx={{ textAlign: 'center', color: 'orange.main', fontStyle: 'italic' }}>
              {currentUser.has_class_rep_discount ?
                'Class-Rep'
              : discountPercent ?
                'Member'
              : 'Sale'}{' '}
              Price - {discountStr} off
            </Typography>
          )}
        </div>
        <StudySessionCardActionButton
          ButtonProps={{ fullWidth: true }}
          courseSubscription={courseSubscription}
          loginRedirect="?checkout=true"
          onRegister={onRegister}
          onRegisterFree={onRegisterFree}
          onSubscribeCheckout={onRegister}
          studySession={studySession}
          studySessionFreeRegisterStatus={studySessionFreeRegisterStatus}
          subscription={subscription}
          userCourse={userCourse}
        />
        {renderFreeWeeklyCTA()}
        {renderDiscountBlock()}
      </Box>
      {subscriptionCheckoutDialogOpened && (
        <SubscriptionCheckout onClose={subscriptionCheckoutDialogActions.close} open={subscriptionCheckoutDialogOpen} />
      )}
    </>
  );

  function renderFreeWeeklyCTA() {
    const freeWeeklySession = weeklyBundleGrouping?.find(ss => !ss.price && ss.purchase_type === 'standard');
    if (!freeWeeklySession) return;

    if (isStudySessionUserRegistered(freeWeeklySession.current_user_study_session_user)) {
      const freeWeeklyRelease = navItems.find(i => i.study_session === freeWeeklySession)?.release_date;
      return (
        <>
          <Button disabled startIcon={<FontAwesomeIcon icon={faBadgeCheck} />} variant="contained">
            Registered for Free 1st Week
          </Button>
          {freeWeeklyRelease > new Date() && (
            <Typography sx={{ textAlign: 'center', color: 'text.secondary' }} variant="body2">
              First week releases on {formatDate(freeWeeklyRelease, 'upcomingDateAndTime')}
            </Typography>
          )}
        </>
      );
    }
    return (
      <LoginProtected actionText="Register" messagingProduct="live-prep">
        <Button
          disabled={!!studySessionFreeRegisterStatus?.fetching}
          fullWidth
          onClick={() => {
            onRegisterFree(freeWeeklySession.id);
            if (activeNavItem.study_session === freeWeeklySession) return;
            onSelectItemIdx(~(~navItems.findIndex(i => i.study_session === freeWeeklySession) || -1));
          }}
          variant="outlined"
        >
          Watch 1st Week Free
        </Button>
      </LoginProtected>
    );
  }

  function renderDiscountBlock() {
    if (discountPercent || !SUBSCRIPTION_PRODUCT_DISCOUNT_PERCENT) return;
    const [newPrice] = getStudySessionPricing(studySession, SUBSCRIPTION_PRODUCT_DISCOUNT_PERCENT);
    if (newPrice >= price) return;
    return (
      <Box
        sx={{
          display: 'flex',
          columnGap: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          textAlign: 'center',
          '@container (max-width: 500px)': { flexDirection: 'column', mb: -1 },
        }}
      >
        <Typography variant="body2">
          {WIZE_SUBSCRIPTION_NAME} members save {regularPrice ? 'an additional ' : ''}
          <strong>{numberToCurrency(price - newPrice)}</strong>
        </Typography>
        <Button onClick={subscriptionCheckoutDialogActions.open} size="small">
          Subscribe
        </Button>
      </Box>
    );
  }
}

function StudySessionItemsNavigation({ activeItemIdx, course, instructor, navItems, onSelectItemIdx, studySession }) {
  if (navItems.length < 2) return;

  const dateLabel = getStudySessionDateLabel(studySession);

  return (
    <Box sx={{}}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, pb: 1 }}>
        <Typography variant="subtitle1">{pluralize(navItems.length, dateLabel)}</Typography>
        <Typography sx={{ color: 'text.secondary' }} variant="body2">
          {activeItemIdx + 1}/{navItems.length}
        </Typography>
      </Box>
      <Box
        sx={{
          maxHeight: 350,
          overflowY: 'auto',
          '& > .navItem': {
            position: 'relative',
            width: '100%',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            gap: 1,
            '&.navItem_active': { backgroundColor: 'background.paper2' },
            '& > .ssDateThumbnailWrapper': {
              flex: '0 0 100px',
              borderRadius: '4px',
              overflow: 'hidden',
              '& .ssDateThumbnailDuration': {
                position: 'absolute',
                borderRadius: '4px',
                px: 0.5,
                bottom: '5%',
                right: '3%',
                backgroundColor: 'action.active',
                color: 'background.paper',
                fontSize: 12,
                fontWeight: 'fontWeightBold',
              },
            },
            '& > .ssDateDetails': { flex: 1 },
            '& > .ssDateTag': { position: 'absolute', top: 0, right: 0, mt: 1.5, mr: 2 },
          },
        }}
      >
        {navItems.map((item, idx) => {
          return (
            <ButtonBase
              key={idx}
              className={classNames('navItem', { navItem_active: activeItemIdx === idx })}
              onClick={() => onSelectItemIdx(idx)}
            >
              <AspectRatioWrapper className="ssDateThumbnailWrapper">
                <StudySessionCoverArt
                  course={course}
                  instructor={instructor}
                  partLabel={navItems.length > 1 && item.label}
                  studySession={studySession}
                />
                {item.estimated_duration && (
                  <span className="ssDateThumbnailDuration">
                    {item.actual_duration ?
                      formatSeekTime(item.actual_duration)
                    : `~${pluralize(...approximateDuration(item.estimated_duration, { excludeDays: true, shortName: true }))}`
                    }
                  </span>
                )}
              </AspectRatioWrapper>
              <div className="ssDateDetails">
                <Typography variant="subtitle1">{item.label}</Typography>
                {renderStatusInfo(item)}
              </div>
              {!item.study_session.price && !item.recording_excluded && (
                <Label className="ssDateTag" label="Watch Free!" size="small" variant="light" />
              )}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );

  /*
  Item Timing info:
  is_live_now: bool,
  live_start_time: Date (if upcoming live or recording_excluded)
  live_end_time
  release_date: expected/actual release date (if past no-recording, say "soon")
  recording: attachment
 */

  function renderStatusInfo(item) {
    if (item.is_live_now) {
      return <Typography variant="subtitle2">LIVE NOW</Typography>;
    }
    if (item.live_start_time) {
      if (item.live_start_time > new Date()) {
        return (
          <Typography sx={{ color: 'text.secondary' }} variant="body2">
            {`Live: ${formatDate(item.live_start_time, 'upcomingDateShort')}, ${formatDateRange(item.live_start_time, item.live_end_time, 'time').replaceAll(' ', '')}`}
          </Typography>
        );
      }
      return (
        <Typography sx={{ color: 'text.secondary' }} variant="body2">
          {`Live ${formatDate(item.live_start_time, 'fromNow')} (not recorded)`}
        </Typography>
      );
    }
    if (item.release_date) {
      if (item.release_date > new Date()) {
        return (
          <Typography sx={{ color: 'text.secondary' }} variant="body2">
            {`${item.study_session.disable_zoom ? 'Releases' : 'Recording releases'} ${formatDate(item.release_date, 'fromNow')}`}
          </Typography>
        );
      }
      if (item.recording) {
        return null; //<Typography variant="body2">Released {formatDate(item.release_date, 'fromNow')}</Typography>;
      }
      return (
        <Typography sx={{ color: 'text.secondary' }} variant="body2">
          {item.study_session.disable_zoom ? 'Releases soon' : 'Recording coming soon'}
        </Typography>
      );
    }
    return (
      <Typography sx={{ color: 'text.secondary' }} variant="body2">
        No Recording
      </Typography>
    );
  }
}

function StudySessionFeedbackCard({ course, currentUser, instructor, studySession }) {
  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6">Win a $100 Gift Card</Typography>
      <Typography sx={{ mt: 1, mb: 2 }}>
        Answer a quick survey and you'll be entered to win a $100 Amazon Gift Card. Your feedback will remain anonymous.
      </Typography>
      <Button
        component="a"
        fullWidth
        href={`https://form.typeform.com/to/jUOD6Jwa#${queryString.stringify({
          instructor_name: instructor?.name,
          email: currentUser.email,
          course_code: course.code,
          school_name: course.school?.name,
          study_session_id: studySession.id,
          study_session_type: studySession.type,
        })}`}
        nativeButton={false}
        rel="noreferrer"
        target="_blank"
        variant="contained"
      >
        Leave Feedback
      </Button>
    </Paper>
  );
}

function OtherStudySessions({ studySession, studySessions: studySessionsProp }) {
  const isWeekly = studySession.type === 'weekly_tutorial';
  const studySessions = studySessionsProp.filter(isStudySessionListable);
  const weeklyBundle = studySessions.filter(ss => ss.type === 'weekly_tutorial').sort(sortBy('-price'))[0];
  const isWeeklyPurchased = isStudySessionUserRegistered(weeklyBundle?.current_user_study_session_user);

  const otherSessions = [
    !isWeekly && weeklyBundle,
    ...studySessions.filter(
      ss =>
        ss.type !== 'weekly_tutorial' &&
        ss.id !== studySession.id &&
        (!isWeeklyPurchased || ss.type !== 'exam_prep' || ss.disable_exam_prep_warning),
    ),
  ].filter(Boolean);
  if (!otherSessions.length) return;
  return (
    <section>
      <Typography variant="h6">Other Grade Boosters for this Course</Typography>
      <div
        css={({ palette, shape, spacing }) => ({
          marginTop: spacing(1),
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(2),
          '& > div': {
            backgroundColor: palette.background.paper,
            padding: spacing(2),
            borderRadius: shape.borderRadiusSm,
            display: 'flex',
            alignItems: 'flex-start',
            gap: spacing(2),
            '& > img': {
              flex: '0 0 auto',
              width: 75,
              objectFit: 'contain',

              borderRadius: shape.borderRadiusSm,
            },
          },
        })}
      >
        {otherSessions.map(ss => {
          const ssTypeConfig = STUDY_SESSION_TYPES[ss.type];
          return (
            <div key={ss.id}>
              <img
                alt={ssTypeConfig.label}
                css={({ palette }) => ({ backgroundColor: palette[ssTypeConfig.color].light })}
                src={ssTypeConfig.image}
              />
              <div>
                <MuiLink component={Link} to={`../${ssTypeConfig.slug}/${ss.id}`} underline="hover" variant="subtitle1">
                  {ss.title}
                </MuiLink>
                <Typography variant="body2">{ssTypeConfig.shortDescription || ssTypeConfig.description}</Typography>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const FAQsSection = memo(function FAQsSection({ className, studySession }) {
  return (
    <Paper className={className} elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6">FAQs</Typography>
      <Divider sx={{ mb: 3, mt: 2 }} />
      <StudentCoursePageFAQsList boosterType={studySession.type} />
    </Paper>
  );
});

function getItemReleaseInfo(item) {
  const res = {};
  const ssd = item.ssd;
  const studySession = item.study_session;
  const { booklet_excluded, recording_excluded, solutions_excluded } =
    (studySession.dates.length > 1 && ssd) || studySession;
  if (ssd) {
    const startTime = new Date(ssd.start_time);
    const endTime = new Date(ssd.end_time);
    if (item.date_status === 'live') res.is_live_now = true;
    if (!studySession.disable_zoom) {
      if (startTime > new Date() || recording_excluded) {
        res.live_start_time = startTime;
        res.live_end_time = endTime;
      }
    }
    if (!recording_excluded) {
      if (studySession.disable_zoom) {
        res.release_date = startTime;
      } else {
        const maxReleaseDate = addDate(endTime, { hours: 2 });
        const releaseDate =
          item.recording ?
            new Date(clamp(new Date(item.recording.created_at), addDate(endTime, { minutes: 30 }), maxReleaseDate))
          : maxReleaseDate;
        res.release_date = releaseDate;
      }
    }
    if (!booklet_excluded) {
      res.booklet_release_date = studySession.disable_zoom ? startTime : subtractDate(startTime, { days: 1 });
    }
    if (!solutions_excluded) {
      res.solutions_release_date = studySession.disable_zoom ? startTime : endTime;
    }
  } else {
    const endTime = getStudySessionEndTime(studySession);
    res.release_date =
      (endTime && new Date(endTime)) || (item.recording?.created_at && new Date(item.recording.created_at));
    if (!studySession.dates.length) {
      res.booklet_release_date = !booklet_excluded && new Date(0);
      res.solutions_release_date = !solutions_excluded && new Date(0);
    }
  }

  const recordingDuration = item.recording?.duration;
  res.actual_duration = recordingDuration;
  res.estimated_duration =
    recordingDuration ||
    (item.ssd && !item.recording_excluded && (new Date(item.ssd.end_time) - new Date(item.ssd.start_time)) / 1000);

  return res;
}

const ExamPrepWarningBanner = memo(function ExamPrepWarningBanner({ studySession, studySessions }) {
  if (
    !studySession.price ||
    studySession.disable_exam_prep_warning ||
    studySession.type !== 'exam_prep' ||
    isStudySessionUserRegistered(studySession.current_user_study_session_user)
  ) {
    return;
  }
  const isRegisteredForWeekly = studySessions.some(
    ss => ss.price && ss.type === 'weekly_tutorial' && isStudySessionUserRegistered(ss.current_user_study_session_user),
  );
  if (!isRegisteredForWeekly) return;

  const availableExamReview =
    studySession.assessment_id &&
    studySessions.find(ss => ss.type === 'exam_review' && ss.assessment_id === studySession.assessment_id);

  return (
    <Box sx={{ backgroundColor: 'yellow.light', py: 2, px: 3, textAlign: 'center', '& > *': { display: 'inline' } }}>
      <Typography sx={{ color: 'yellow.dark' }} variant="subtitle1">
        <FontAwesomeIcon icon={faCircleInfo} /> Heads Up!
      </Typography>{' '}
      <Typography>You already own this content because you have purchased the weekly tutorial bundle. </Typography>
      {availableExamReview && (
        <Typography>
          Check out the{' '}
          <MuiLink
            component={Link}
            sx={{ fontWeight: 'subtitle1.fontWeight', color: 'inherit' }}
            to={`../${STUDY_SESSION_TYPES.exam_review.slug}/${availableExamReview.id}`}
            underline="hover"
          >
            Mock Exam Walkthroughs
          </MuiLink>{' '}
          for your exam.
        </Typography>
      )}
    </Box>
  );
});
